import PropTypes from 'prop-types';
import { useState } from 'react';
import Dropzone from 'react-dropzone';
import intl from 'react-intl-universal';
import { DropzoneContainer, DropzoneOverlay } from './styles';
import { generateDirectoryList } from 'views/New/utils/utils';
import pxToRem from 'assets/theme/functions/pxToRem';
import { Button } from '@mui/material';
import theme from 'assets/theme';
import { zIndexValues } from 'utils/styles';
import logger from 'utils/logger';
import DuplicatedFileModal from 'components/DropZone/components/duplicatedFileModal';
import InvalidFileModal from 'components/DropZone/components/invalidFileModal';
import ConfirmDeleteModal from 'components/DropZone/components/ConfirmDeleteModal';
import { isDeepEqual } from 'utils/equality';
import {
  fileTypeIsValid,
  getStructuredFiles,
  calculateFolderSize,
  findDuplicateAttachments,
  checkIfCanReplace,
} from 'utils/fileHandling';
import { removeLeadingSlash } from 'utils/misc';

const propTypes = {
  existingStructuredAttachments: PropTypes.arrayOf(PropTypes.object),
  structuredAttachments: PropTypes.arrayOf(PropTypes.object).isRequired,
  setStructuredAttachments: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
  isModal: PropTypes.bool,
  isKenshin: PropTypes.bool,
};

export const ACTIONS = {
  REPLACE: 'REPLACE',
  IGNORE: 'IGNORE',
  CANCEL: 'CANCEL',
  ADD_AS_NEW: 'ADD_AS_NEW',
};

//! Single file size is 5GB
const MAX_TOTAL_SIZE = 5;
const MAX_TOTAL_SIZE_IN_GB = MAX_TOTAL_SIZE * 1024 * 1024 * 1024;
const log = logger.createLogger('DoCloudDropzone');

const DoCloudDropzone = ({
  existingStructuredAttachments,
  structuredAttachments,
  setStructuredAttachments,
  darkMode,
  isModal = false,
  isKenshin = false,
}) => {
  const [newStructuredAttachments, setNewStructuredAttachments] = useState([]);

  const [duplicatedAttachments, setDuplicatedAttachments] = useState([]);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [oversizedFiles, setOversizedFiles] = useState([]);

  const [showWarnInvalidFileTypes, setShowWarnInvalidFileTypes] =
    useState(false);
  const [confirmDeleteFile, setConfirmDeleteFile] = useState(false);
  const [fileToDelete, setFileToDelete] = useState({});

  const handleClosingWarnInvalidFileWindow = () => {
    setInvalidFiles([]);
    setOversizedFiles([]);
    setShowWarnInvalidFileTypes(false);
  };

  const handleDuplicateAttachments = async (action) => {
    switch (action) {
      case ACTIONS.REPLACE: {
        const tmp = structuredAttachments.filter(
          (item) => !duplicatedAttachments.some((x) => item.name === x.name)
        );
        setStructuredAttachments([...tmp, ...newStructuredAttachments]);
        break;
      }
      case ACTIONS.IGNORE: {
        const nonDups = newStructuredAttachments.filter(
          (item) => !duplicatedAttachments.some((x) => item.name === x.name)
        );
        setStructuredAttachments([...structuredAttachments, ...nonDups]);
        break;
      }
      case ACTIONS.ADD_AS_NEW: {
        // The NEW attachments to add
        const newStructuredAttachmentsWithoutDups =
          newStructuredAttachments.filter(
            (item) => !duplicatedAttachments.some((x) => item.name === x.name)
          );

        // The attachments with existing to calculate the index
        const structuredAttachmentsWithExistingAttachments = [
          ...structuredAttachments,
          ...existingStructuredAttachments,
        ];

        const newDuplicatedStructuredAttachments = [];

        duplicatedAttachments.forEach((item) => {
          const sameNameItems =
            structuredAttachmentsWithExistingAttachments.filter((x) => {
              const filename = item.name.split('.')[0];
              return x.name.includes(filename);
            });
          const newIndex = sameNameItems.length;

          newDuplicatedStructuredAttachments.push({
            ...item,
            name: `${item.name.split('.')[0]} (${newIndex}).${item.name
              .split('.')
              .pop()}`,
          });
        });

        setStructuredAttachments([
          ...structuredAttachments,
          ...newDuplicatedStructuredAttachments,
          ...newStructuredAttachmentsWithoutDups,
        ]);
        break;
      }
      case ACTIONS.CANCEL:
        break;
      default:
        break;
    }
    setNewStructuredAttachments([]);
    setDuplicatedAttachments([]);
  };

  const handleFileDelete = (item) => async () => {
    setFileToDelete(item);
    setConfirmDeleteFile(true);
  };

  // TODO Check the existing structured attachments and the new structured attachments as well
  // The existing structured attachments will not be replaced
  // Handle files from the dropzone
  const handleFiles = async (files) => {
    // filter out the files that are too large
    const checkOversizedFiles = files.filter(
      (file) => file.size > MAX_TOTAL_SIZE_IN_GB
    );

    // Check if file exceeds the limit
    if (checkOversizedFiles.length > 0) {
      log.warn('Individual file size exceeds limit:', {
        oversizedFiles: oversizedFiles.map((file) => ({
          name: file.name,
          size: file.size,
          sizeInGB: (file.size / (1024 * 1024 * 1024)).toFixed(2),
        })),
        limit: MAX_TOTAL_SIZE_IN_GB,
      });
      setOversizedFiles([...checkOversizedFiles]);
      files = files.filter((file) => !checkOversizedFiles.includes(file));
    }

    // Checking invalid files
    const checkInvalidFiles = [];
    for (const file of files) {
      if (!(await fileTypeIsValid(file))) {
        checkInvalidFiles.push(file);
      }
    }

    if (checkInvalidFiles.length > 0) {
      log.warn('Invalid file types:', {
        invalidFiles: checkInvalidFiles.map((file) => ({
          name: file.name,
          type: file.type,
        })),
      });
      setInvalidFiles([...checkInvalidFiles]);
      files = files.filter((file) => !checkInvalidFiles.includes(file));
    }

    // Handle all the valid files for the duplicates as some of the paths have leading slash
    files = files.map((file) => {
      const item = {
        type: 'file',
        file: file,
        name: file.name,
        fileFullPath: removeLeadingSlash(file.path),
        size: file.size,
      };
      return item;
    });

    if (checkInvalidFiles.length > 0 || checkOversizedFiles.length > 0) {
      setShowWarnInvalidFileTypes(true);
    }

    // Checks the structure of the files to see if there are duplicates
    const newItems = getStructuredFiles(files);
    newItems.forEach((item) => {
      calculateFolderSize(item);
    });

    const dups = findDuplicateAttachments(
      structuredAttachments,
      newItems,
      existingStructuredAttachments
    );
    if (dups.length > 0) {
      setDuplicatedAttachments(dups);
      setNewStructuredAttachments(newItems);
    } else {
      setStructuredAttachments([...structuredAttachments, ...newItems]);
    }
  };

  const zIndexValue = isModal ? zIndexValues.modal4 : zIndexValues.dropdown;
  const zIndexButton = zIndexValue + 1;

  return (
    <>
      <Dropzone onDrop={handleFiles} noClick={true}>
        {({ getRootProps, getInputProps, open }) => {
          return (
            <div {...getRootProps()}>
              <input {...getInputProps()} data-testid="file-input" />
              <DropzoneContainer
                containsFiles={structuredAttachments.length > 0}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    zIndex: isModal
                      ? zIndexValues.modal4 + 1
                      : zIndexValues.dropdown + 1,
                  }}>
                  {generateDirectoryList(
                    structuredAttachments,
                    handleFileDelete
                  )}
                </div>
                <DropzoneOverlay
                  darkMode={darkMode}
                  containsFiles={structuredAttachments.length > 0}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: zIndexValue,
                  }}>
                  <span>{intl.get('new_text_dropzone_message')}</span>
                  <span
                    style={{
                      fontSize: '12px',
                      opacity: 0.7,
                      marginTop: '5px',
                      display: 'block',
                    }}>
                    {intl.get('dropzone_total_size_exceeded_message', {
                      maxSize: MAX_TOTAL_SIZE,
                    })}
                  </span>
                  <span
                    style={{
                      fontSize: '12px',
                      opacity: 0.7,
                      marginTop: '5px',
                      display: 'block',
                      color: 'red',
                    }}>
                    {intl.get('dropzone_compressed_files_message')}
                  </span>
                  {isKenshin && (
                    <span
                      style={{
                        fontSize: '12px',
                        opacity: 0.7,
                        marginTop: '5px',
                        display: 'block',
                        color: 'red',
                      }}>
                      {intl.get('dropzone_only_acceptable_dicom_files_message')}
                    </span>
                  )}
                </DropzoneOverlay>
              </DropzoneContainer>
              <Button
                data-testid="new_send_button"
                color="primary"
                variant="contained"
                onClick={open}
                style={{
                  fontSize: pxToRem(18),
                  position: 'absolute',
                  left: '50%',
                  zIndex: zIndexButton,
                  color: '#FFF',
                  backgroundColor: theme.palette.primary.main,
                  bottom: '-20px',
                  minWidth: '300px',
                  borderRadius: '30px',
                  transform: 'translateX(-50%)',
                }}>
                {intl.get('new_text_select_files')}
              </Button>
            </div>
          );
        }}
      </Dropzone>

      {/* Modal of duplicated files for dropzone */}
      {/* TODO Need to add a check if it existing attachments */}
      <DuplicatedFileModal
        duplicatedAttachments={duplicatedAttachments}
        handleDuplicateAttachments={handleDuplicateAttachments}
        canReplace={checkIfCanReplace(
          newStructuredAttachments,
          existingStructuredAttachments
        )}
      />
      {/* Modal of invalid files for dropzone */}
      <InvalidFileModal
        showWarnInvalidFileTypes={showWarnInvalidFileTypes}
        handleClosingWarnInvalidFileWindow={handleClosingWarnInvalidFileWindow}
        invalidFiles={invalidFiles}
        oversizedFiles={oversizedFiles}
      />
      {/* To delete a file from the dropzone */}
      <ConfirmDeleteModal
        isOpened={confirmDeleteFile}
        onConfirm={() => {
          setStructuredAttachments(
            structuredAttachments.filter(
              (item) => !isDeepEqual(item, fileToDelete)
            )
          );
          setFileToDelete({});
          setConfirmDeleteFile(false);
        }}
        onCancel={() => setConfirmDeleteFile(false)}
        fileToDelete={fileToDelete?.name}
      />
    </>
  );
};

DoCloudDropzone.propTypes = propTypes;

export default DoCloudDropzone;
